<template>
  <div class="relative tag-input-container">
    <div v-if="!disabled" class="relative">
      <input
        type="text"
        :class="inputClass || 'w-full px-3 py-2 border rounded-md'"
        :placeholder="placeholder"
        v-model="searchQuery"
        :disabled="disabled"
        @keydown.enter.prevent="addTag"
        @keydown.backspace="handleBackspace"
        @focus="showDropdown = true"
        @input="onInput"
      />
      <div v-if="showDropdown && allowNew && searchQuery && !disabled" 
        class="absolute top-11 -left-2 w-full bg-white m-2 rounded-md shadow-md border transition-all duration-300"
        style="z-index: 60;">
        <Loading v-if="loading" class="p-2" />
        <ul v-if="!loading" class="overflow-y-auto max-h-48">
            <li v-for="tag in itemsLoaded"
                :key="tag.id"
                @click="selectTag(tag)"
                class="bg-white p-2 hover:brightness-95 cursor-pointer flex items-center gap-2"
                :style="tagStyle(tag)"
            >
            {{ getTagName(tag) }}
          </li>
        </ul>
        <ul>
          <li
            v-if="allowNew && searchQuery && !tagExists"
            @click="addTag"
            class="bg-white border border-t-1 border-gray-200 p-2 hover:bg-green-200 cursor-pointer"
          >
            Add "{{ searchQuery }}" as new tag
          </li>
          
        </ul>
      </div>
    </div>

    <div ref="tagsContainer" class="flex flex-wrap gap-2 mt-2 p-2 cursor-default">
      <span
        v-for="tag in modelValue"
        :key="tag.id"
        class="rounded-md pl-2 pr-1 text-sm tag-item cursor-move"
        :style="tagStyle(tag)"
      >
        {{ getTagName(tag) }}
        <button
            v-if="!disabled"
            type="button"
            class="hover:text-red-500 hover:text-white rounded-full p-1"
            @click="removeTag(tag)"
            >
          ×
        </button>
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits, watch } from 'vue'   
import { useI18n } from 'vue-i18n';
import {debounce} from '@/service/debounce.js';
import { generateTagColors, getLocalizedTagName } from '@/service/tagColor.js';

const { locale } = useI18n();

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  availableTags: {
    type: Array,
    default: () => []
  },
  allowNew: {
    type: Boolean,
    default: true
  },
  placeholder: {
    type: String,
    default: 'Add tags...'
  },
  items: {
    type: Array,
    default: () => []
  },
  inputClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'search']);
const loading = ref(false);
const itemsLoaded = ref([]);

const getTagName = (tag) => {
  return getLocalizedTagName(tag, locale.value);
};

const tagStyle = (tag) => {
    const bg = tag.color_background;
    const fg = tag.color_font;
    if (bg && fg) {
        return { backgroundColor: bg, color: fg };
    }
    const name = typeof tag === 'object' && tag !== null ? tag.name : tag;
    if (name) {
        const c = generateTagColors(name);
        return { backgroundColor: c.color_background, color: c.color_font };
    }
    return { backgroundColor: '#f3f4f6', color: '#374151' };
};

const searchQuery = ref('')
const showDropdown = ref(false)

watch( () => props.items, (newValue) => {
  loading.value = false;
  itemsLoaded.value = newValue;
}, { immediate: true }) 

const tagExists = computed(() => {
  const normalizedQuery = searchQuery.value.toLowerCase().trim()
  return props.availableTags.some(tag => 
    (typeof tag === 'object' ? tag.name : tag).toLowerCase() === normalizedQuery ||
    getTagName(tag).toLowerCase() === normalizedQuery
  ) || props.modelValue.some(tag => 
    (typeof tag === 'object' && tag !== null ? tag.name : tag).toLowerCase() === normalizedQuery ||
    getTagName(tag).toLowerCase() === normalizedQuery
  )
})

const addTag = () => {
  if (!searchQuery.value.trim()) return

  if (!tagExists.value && props.allowNew) {
    const newTag = {
      id: Date.now(),
      name: searchQuery.value.trim()
    }
    emit('update:modelValue', [...props.modelValue, newTag])
  }
  
  searchQuery.value = ''
  showDropdown.value = false
  itemsLoaded.value = [];
}

const selectTag = (tag) => {
  const tagName = typeof tag === 'object' ? tag.name : tag;
  if (!props.modelValue.some(t => (typeof t === 'object' ? t.name : t) === tagName)) {
    emit('update:modelValue', [...props.modelValue, tag])
  }
  searchQuery.value = ''
  showDropdown.value = false
}

const removeTag = (tag) => {
  const tagName = typeof tag === 'object' ? tag.name : tag;
  emit('update:modelValue', props.modelValue.filter(t => (typeof t === 'object' ? t.name : t) !== tagName))
}

const handleBackspace = (event) => {
  if (searchQuery.value === '' && props.modelValue.length > 0) {
    event.preventDefault()
    removeTag(props.modelValue[props.modelValue.length - 1])
  }
}

const onInput = debounce(() => {
  loading.value = true;
  showDropdown.value = true
  emit('search', searchQuery.value)
}, 300)

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.tag-input-container')) {
    showDropdown.value = false
  }
}

import Sortable from 'sortablejs';

const tagsContainer = ref(null);

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  if (tagsContainer.value) {
    Sortable.create(tagsContainer.value, {
      animation: 150,
      draggable: '.tag-item',
      onEnd: (evt) => {
        const newTags = [...props.modelValue];
        const item = newTags.splice(evt.oldIndex, 1)[0];
        newTags.splice(evt.newIndex, 0, item);
        emit('update:modelValue', newTags);
      }
    });
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* .tag-input-container {
  width: 100%;
}

.input-wrapper {
  position: relative;
  margin-bottom: 0.5rem;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item.new-item {
  color: #2196F3;
  font-style: italic;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background-color: #e3f2fd;
  border-radius: 16px;
  padding: 4px 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
}

.remove-tag {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  padding: 0 2px;
  line-height: 1;
}

.remove-tag:hover {
  color: #f44336;
} */
</style> 